import {
    of,
    tap,
    combineLatest,
    switchMap,
    first,
    merge,
    fromEvent,
    startWith,
    map,
    distinctUntilChanged,
    forkJoin,
    delay,
    EMPTY,
    Subject,
} from 'rxjs';

import { replaceContent, renderTemplate } from './view.js';
import { dishesById$ } from './api.js';
import { findParent } from './dom.js';

let nextRadioNameId = 1;

export function createDishSelect({ container, dishList$, selectedDishId$, disabled$ }) {
    return of(undefined).pipe(
        switchMap(() => {
            let radioNameId = nextRadioNameId++;
            let root = renderTemplate('dish-select');
            replaceContent(container, root);
            let dishInputById = new Map();

            let filterExpressionInput = root.querySelector('.filter-expression');
            let filterExpression$ = fromEvent(filterExpressionInput, 'keyup').pipe(
                delay(0),
                startWith(undefined),
                map(() => (filterExpressionInput.value || '').trim()),
                distinctUntilChanged()
            );

            let renderDishList$ = combineLatest([dishList$, dishesById$]).pipe(
                switchMap(([dishList, dishesById]) =>
                    forkJoin([
                        selectedDishId$.pipe(first()),
                        filterExpression$.pipe(first()),
                        disabled$.pipe(first()),
                    ]).pipe(
                        tap(([selectedDishId, filterExpression, disabled]) => {
                            let container = root.querySelector('.list');
                            container.innerHTML = '';
                            root.querySelector('.loading').style.display = 'none';
                            let inputs = new Map();
                            for (let dish of dishList) {
                                let dishTitle = dishesById.get(dish.id).title;
                                let dishEl = renderTemplate('dish-select-dish');
                                dishEl.setAttribute('data-dish-id', '' + dish.id);
                                dishEl.querySelector('.dish-title').textContent = dishTitle;
                                let dishInput = dishEl.querySelector('.dish-input');
                                dishInput.name = `dish-select-${radioNameId}`;
                                dishInput.value = dish.id;
                                dishInput.checked = dish.id === selectedDishId;
                                dishInput.disabled = disabled;
                                dishInput.setAttribute(
                                    'data-filter-title',
                                    dishTitle.toLowerCase()
                                );
                                dishInput.addEventListener('change', handleSelectDish, false);
                                inputs.set(dish.id, dishInput);
                                container.appendChild(dishEl);
                            }
                            dishInputById = inputs;
                            applyFilter(filterExpression);
                        })
                    )
                )
            );

            let updateSelectedDish$ = selectedDishId$.pipe(
                tap((selectedDishId) => {
                    for (let [dishId, input] of dishInputById) {
                        input.checked = dishId === selectedDishId;
                    }
                })
            );

            let updateFilter$ = filterExpression$.pipe(tap(applyFilter));

            let updateDisabled$ = disabled$.pipe(
                distinctUntilChanged(),
                tap((disabled) => {
                    for (let [dishId, input] of dishInputById) {
                        input.disabled = disabled;
                    }
                })
            );

            let silentObservables = merge(
                renderDishList$,
                updateSelectedDish$,
                updateFilter$,
                updateDisabled$
            ).pipe(switchMap(() => EMPTY));

            let userSelectedDishId$ = new Subject();

            return merge(silentObservables, userSelectedDishId$);

            function applyFilter(filterExpression) {
                let expr = filterExpression.toLowerCase();
                for (let [dishId, input] of dishInputById) {
                    let title = input.getAttribute('data-filter-title');
                    let li = findParent(input, (el) => el.tagName === 'LI');
                    li.style.display = !expr || title.indexOf(expr) !== -1 ? null : 'none';
                }
            }

            function handleSelectDish(event) {
                // prevent default so that the input can only be
                // selected via the selectedDishId$ observable.
                event.preventDefault();
                let selectedDishId = parseInt(event.target.value);
                userSelectedDishId$.next(selectedDishId);
            }
        })
    );
}
